import { useEffect, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '../api/client'
import {
  PROFILE_KEY,
  changeAdminPassword,
  fetchAdminProfile,
  updateAdminProfile,
  type AdminProfile,
} from '../api/profile'
import { useAuth } from '../AuthContext'
import { Callout } from '../components/Callout'
import { SaveToast } from '../components/SaveToast'
import { roleLabelFor } from '../nav'

/** Same minimum the backend applies (`auth._MIN_PASSWORD_LEN`). */
const MIN_PASSWORD_LEN = 12

/** One input treatment, shared with LoginPage, so the fields cannot drift. */
const INPUT_CLASS =
  'h-11 w-full rounded-[10px] border border-line-2 bg-bg-app px-3 text-sm text-ink-1 placeholder:text-ink-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

const BUTTON_CLASS =
  'inline-flex h-11 items-center justify-center rounded-[8px] bg-accent px-4 text-sm font-medium text-accent-on hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60'

function nowTime() {
  return new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** A field-level error. A form-level "something went wrong" for a 409 or a 401
 *  is a dead end for the person who has to fix it, so both land on their field. */
function FieldError({ id, children }: { id: string; children: string }) {
  return (
    <p id={id} role="alert" className="text-sm font-medium text-bed-bad">
      {children}
    </p>
  )
}

/**
 * `/admin/profile` — the admin edits their own name, email and password
 * (M24-T08, the `Self` tier of ADR-0051).
 *
 * **Reached from the user card menu, not the sidebar.** The sidebar lists
 * queues of work; a personal settings page in that list is a category error
 * every admin then scans past ten times a day.
 *
 * **Role is shown and not editable.** Changing it is Reserved — the server
 * refuses it with 422 rather than dropping it — so the page says who to ask
 * instead of offering a control that would fail.
 */
export function ProfilePage() {
  const { token } = useAuth()

  const { data: profile, isPending } = useQuery({
    queryKey: PROFILE_KEY,
    queryFn: () => fetchAdminProfile(token),
    enabled: Boolean(token),
  })

  if (isPending || !profile) {
    return (
      <section className="flex max-w-2xl flex-col gap-8">
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Your profile
        </h1>
        <p className="text-sm text-ink-3">Loading your details…</p>
      </section>
    )
  }

  // Keyed on the loaded row, so the form's initial values come from useState
  // initialisers rather than an effect that copies props into state. That
  // pattern is what `react-hooks/set-state-in-effect` exists to prevent, and
  // remounting is the honest way to say "these are the values as loaded".
  return <ProfileForm key={profile.user_id} profile={profile} />
}

function ProfileForm({ profile }: { profile: AdminProfile }) {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const [fullName, setFullName] = useState(profile.full_name ?? '')
  const [email, setEmail] = useState(profile.email ?? '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [toastTime, setToastTime] = useState<string | null>(null)

  // Password-change form, deliberately separate: it is a different endpoint,
  // a different failure mode, and mixing them would make one submit button
  // mean two things.
  const [pwCurrent, setPwCurrent] = useState('')
  const [pwNew, setPwNew] = useState('')
  const [pwConfirm, setPwConfirm] = useState('')
  const [pwError, setPwError] = useState<string | null>(null)
  const [pwFieldError, setPwFieldError] = useState<string | null>(null)

  useEffect(() => {
    if (!toastTime) return
    const t = setTimeout(() => setToastTime(null), 3000)
    return () => clearTimeout(t)
  }, [toastTime])

  const emailChanged = email !== (profile.email ?? '')

  const save = useMutation({
    mutationFn: () =>
      updateAdminProfile(token, {
        ...(fullName !== (profile?.full_name ?? '')
          ? { full_name: fullName }
          : {}),
        ...(emailChanged ? { email, current_password: currentPassword } : {}),
      }),
    onSuccess: (updated) => {
      // The sidebar card reads the same query key, so it updates without a
      // reload.
      queryClient.setQueryData(PROFILE_KEY, updated)
      void queryClient.invalidateQueries({ queryKey: PROFILE_KEY })
      setCurrentPassword('')
      setNameError(null)
      setEmailError(null)
      setPasswordError(null)
      setToastTime(nowTime())
    },
    onError: (err) => {
      setNameError(null)
      setEmailError(null)
      setPasswordError(null)
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setEmailError('That email address cannot be used. Try another.')
          return
        }
        if (err.status === 401) {
          setPasswordError('That password is not correct.')
          return
        }
        if (err.code === 'password_required') {
          setPasswordError('Enter your current password to change your email.')
          return
        }
        // 400 as well as 422: the API standardises Pydantic validation
        // failures to 400 (M2-T04), so a malformed address arrives as 400
        // while the route's own refusals are 422.
        if (err.status === 400 || err.status === 422) {
          setEmailError('Check the email address and try again.')
          return
        }
      }
      setEmailError('Could not save. Try again.')
    },
  })

  const changePassword = useMutation({
    mutationFn: () =>
      changeAdminPassword(token, {
        current_password: pwCurrent,
        new_password: pwNew,
      }),
    onSuccess: () => {
      setPwCurrent('')
      setPwNew('')
      setPwConfirm('')
      setPwError(null)
      setPwFieldError(null)
      setToastTime(nowTime())
    },
    onError: (err) => {
      setPwError(null)
      setPwFieldError(null)
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setPwFieldError('That password is not correct.')
          return
        }
        if (err.code === 'password_too_short') {
          setPwError(`Use at least ${MIN_PASSWORD_LEN} characters.`)
          return
        }
      }
      setPwError('Could not change your password. Try again.')
    },
  })

  function onSaveProfile(e: FormEvent) {
    e.preventDefault()
    if (emailChanged && !currentPassword) {
      setPasswordError('Enter your current password to change your email.')
      return
    }
    save.mutate()
  }

  function onChangePassword(e: FormEvent) {
    e.preventDefault()
    if (pwNew !== pwConfirm) {
      setPwError('The two new passwords do not match.')
      return
    }
    changePassword.mutate()
  }

  const roleLabel = roleLabelFor(profile.role)
  const nothingToSave = !emailChanged && fullName === (profile?.full_name ?? '')

  return (
    // A single-column form should not be stretched to 1440 (M23's narrow-width
    // ruling).
    <section className="flex max-w-2xl flex-col gap-8">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Your profile
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          Your own name, email and password. Nothing else about your account is
          editable here.
        </p>
      </div>

      <form onSubmit={onSaveProfile} className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="full_name" className="text-sm font-medium text-ink-2">
            Name
          </label>
          <input
            id="full_name"
            type="text"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            aria-describedby={nameError ? 'full_name-error' : undefined}
            className={INPUT_CLASS}
          />
          {nameError && (
            <FieldError id="full_name-error">{nameError}</FieldError>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="email" className="text-sm font-medium text-ink-2">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={emailError ? 'email-error' : undefined}
            className={INPUT_CLASS}
          />
          {emailError && <FieldError id="email-error">{emailError}</FieldError>}
        </div>

        {/* The consequence appears BEFORE saving, as a plain sentence in the
            form — not a tooltip, not a toast, not placeholder text. There is no
            way to undo this from the outside: we cannot email a confirmation or
            a recovery link, so the warning is the only safeguard the admin
            gets. */}
        {emailChanged && (
          <>
            <Callout>
              You will sign in with this address from now on. We cannot send you
              an email to confirm it, so if it is wrong you will be locked out
              and an administrator will have to restore your access.
            </Callout>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="current_password"
                className="text-sm font-medium text-ink-2"
              >
                Your current password
              </label>
              <input
                id="current_password"
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                aria-describedby={
                  passwordError ? 'current_password-error' : undefined
                }
                className={INPUT_CLASS}
              />
              <p className="text-sm text-ink-3">
                Confirms it is you before your sign-in address changes.
              </p>
              {passwordError && (
                <FieldError id="current_password-error">
                  {passwordError}
                </FieldError>
              )}
            </div>
          </>
        )}

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink-2">Role</span>
          <p className="text-sm capitalize text-ink-1">{roleLabel || '—'}</p>
          <p className="text-sm text-ink-3">
            {profile.role === 'support'
              ? // A coordinator administers no hospital (M36-T03).
                'Your role is set by AMTC. Ask an AMTC administrator if it is wrong.'
              : 'Your role and the hospital you administer are set by AMTC. Ask an AMTC administrator if either is wrong.'}
          </p>
        </div>

        <div>
          <button
            type="submit"
            disabled={save.isPending || nothingToSave}
            className={BUTTON_CLASS}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>

      <hr className="border-line-1" />

      <form onSubmit={onChangePassword} className="flex flex-col gap-5">
        <div>
          <h2 className="font-display text-lg font-semibold text-ink-1">
            Change your password
          </h2>
          <p className="mt-1 text-sm text-ink-3">
            You will stay signed in on this device.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="pw_current"
            className="text-sm font-medium text-ink-2"
          >
            Current password
          </label>
          <input
            id="pw_current"
            type="password"
            autoComplete="current-password"
            required
            value={pwCurrent}
            onChange={(e) => setPwCurrent(e.target.value)}
            aria-describedby={pwFieldError ? 'pw_current-error' : undefined}
            className={INPUT_CLASS}
          />
          {pwFieldError && (
            <FieldError id="pw_current-error">{pwFieldError}</FieldError>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="pw_new" className="text-sm font-medium text-ink-2">
            New password
          </label>
          <input
            id="pw_new"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LEN}
            value={pwNew}
            onChange={(e) => setPwNew(e.target.value)}
            className={INPUT_CLASS}
          />
          <p className="text-sm text-ink-3">
            At least {MIN_PASSWORD_LEN} characters.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="pw_confirm"
            className="text-sm font-medium text-ink-2"
          >
            Confirm new password
          </label>
          <input
            id="pw_confirm"
            type="password"
            autoComplete="new-password"
            required
            value={pwConfirm}
            onChange={(e) => setPwConfirm(e.target.value)}
            aria-describedby={pwError ? 'pw-error' : undefined}
            className={INPUT_CLASS}
          />
          {pwError && <FieldError id="pw-error">{pwError}</FieldError>}
        </div>

        <div>
          <button
            type="submit"
            disabled={changePassword.isPending}
            className={BUTTON_CLASS}
          >
            {changePassword.isPending ? 'Changing…' : 'Change password'}
          </button>
        </div>
      </form>

      {toastTime && <SaveToast time={toastTime} />}
    </section>
  )
}
